import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import type { PropsWithChildren, ReactNode } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

const Container = styled.div`
  grid-area: details;
`

type Props = {
  /**
   * positive integer
   */
  packageNumber?: number
  optionName?: string
  tagLineLabel?: ReactNode
  stayPayRateTier?: App.StayPayTier
  selectedLoyaltyTargetUpgradePackage?: App.Package
}

function AccommodationSameRoomOptionTileDetails(props: PropsWithChildren<Props>) {
  const {
    children,
    packageNumber,
    tagLineLabel,
    optionName,
    stayPayRateTier,
    selectedLoyaltyTargetUpgradePackage,
  } = props

  const intl = useIntl()

  const targetUpgradeName = selectedLoyaltyTargetUpgradePackage?.name
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const packageOptionLabel = packageNumber
    ? intl.formatMessage(
        {
          id: 'e3D2ed1NEgPjpp9ZFe0m',
          defaultMessage: 'Option {number}',
          description: 'Label showing the package option number in the same-room option banner.',
        },
        { number: packageNumber },
      )
    : undefined

  const memberUpgradeLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: 'yJdvrOHnwCY8Ovx0A9dC',
          defaultMessage: '{tierName} member upgrade',
          description: 'Label indicating that this option includes a loyalty member upgrade (same-room banner).',
        },
        { tierName: getLoyaltyTierDisplayName(displayTier) },
      )
    : undefined

  const upgradedToLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: '2-S47pPpwvcaeurwALuu',
          defaultMessage: 'Upgraded to {upgradeName}',
          description: 'Label indicating the selected loyalty upgrade package name (same-room banner).',
        },
        { upgradeName: targetUpgradeName },
      )
    : undefined

  const optionNameLabel = optionName && stayPayRateTier
    ? intl.formatMessage(
        {
          id: '_zgzlQC7jhOJIxFWViD4',
          defaultMessage: '{name}, {count, plural, one {# night free} other {# nights free}}',
          description: 'Package name with included free nights suffix in same-room option banner.',
        },
        { name: optionName, count: stayPayRateTier.freeNights },
      )
    : optionName

  const optionWithUpgradeContent = memberUpgradeLabel && <FormattedMessage
    id="gApD-SMkh4jbSeGytB00"
    defaultMessage="{optionLabel} | <bold>{upgradeLabel}</bold>"
    description="Option number and member upgrade label separated by pipe in same-room option banner"
    values={{
      optionLabel: packageOptionLabel,
      upgradeLabel: memberUpgradeLabel,
      bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
    }}
  />

  return <Container>
    <VerticalSpacer gap={12}>
      <VerticalSpacer gap={4}>
        {!!packageNumber && <Caption variant="large">
          {optionWithUpgradeContent || packageOptionLabel}
        </Caption>}
        {!!tagLineLabel && <LabelGroup>{tagLineLabel}</LabelGroup>}
        {!!targetUpgradeName && <BodyText variant="large" weight="bold">
          {upgradedToLabel}
        </BodyText>}
        {!!optionName && <BodyText
          variant="large"
          weight="bold"
          colour={selectedLoyaltyTargetUpgradePackage ? 'neutral-four' : 'neutral-one'}
          strikeThrough={!!selectedLoyaltyTargetUpgradePackage}
        >
          {optionNameLabel}
        </BodyText>}
      </VerticalSpacer>
      {children}
    </VerticalSpacer>
  </Container>
}

export default AccommodationSameRoomOptionTileDetails
